import { uniq } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useBulkPricingActionsPermissions } from 'src/components/common/BulkActions/BulkPricingActions/useBulkPricingActionsPermissions';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import {
  BulkEditAutoPricingSettingsDialog,
  BulkEditAutoPricingSettingsDialogProps,
} from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog';
import { usePricingConfigFromEventIds } from 'src/hooks/api/usePricingConfigFromEventIds';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  BulkListingPriceCalculationResult,
  CommandOptionType,
  Event,
  Feature,
  Listing,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdatesV2,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_AUTOPRICING_SETTINGS_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchEditAutoPricingSettings } from './LaunchEditAutoPricingSettings';

export const useBulkEditAutoPricingSettings = (
  filterQueryWithEventIds: ListingQuery,
  affectedVisibleListingIds: number[],
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `autopricingSettings-event-${event?.virtId}`
    : INVENTORY_BULK_UPDATE_AUTOPRICING_SETTINGS_UPDATE_KEY;

  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const hasPricingSectionSettingsAcrossEventsFeature = useUserHasFeature(
    Feature.PricingSectionSettingsAcrossEvents
  );
  const hasFixFloorRelativeToMarketFeature = useUserHasFeature(
    Feature.FixFloorRelativeToMarket
  );

  const latestPreviewCalculationRef =
    useRef<BulkListingPriceCalculationResult | null>(null);

  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasAutoPricingFeature, hasEditPricingPermission } =
    useBulkPricingActionsPermissions();

  const {
    eventsTransformed,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();

  const { showErrorDialog } = useClientLogContext();

  const distinctEventIds = useMemo(() => {
    const { eventOrMappingIds } = filterQueryWithEventIds;
    const eventIdsFromSelectedListings = (eventsTransformed ?? [])
      .flatMap((ev) => ev.entities.listings)
      .flatMap(flattenListingGroup)
      .filter(({ id, isLtGrp }) => id > 0 && !isLtGrp)
      .filter(({ id }) => affectedVisibleListingIds.includes(id))
      .map((l) => (l as Listing).viagEvId);
    return uniq([
      ...eventIdsFromSelectedListings,
      ...(eventOrMappingIds ?? [])
        .map((mappingId) => Number(mappingId))
        .filter((id) => !isNaN(id)),
    ]).filter((id: number | null): id is number => !!id);
  }, [affectedVisibleListingIds, eventsTransformed, filterQueryWithEventIds]);

  const pricingConfigFromEvents = usePricingConfigFromEventIds(
    distinctEventIds,
    !hasPricingSectionSettingsAcrossEventsFeature
  );

  const sanitizedEvent = useMemo(() => {
    if (event) {
      return event;
    }

    const { sharedConfigId } = pricingConfigFromEvents.data ?? {};
    if (sharedConfigId && sharedConfigId > 0) {
      return (eventsTransformed ?? [])
        .map((ev) => ev.event)
        .find((ev) => ev.viagId && distinctEventIds.includes(ev.viagId));
    }

    return undefined;
  }, [
    distinctEventIds,
    event,
    eventsTransformed,
    pricingConfigFromEvents.data,
  ]);

  const canUseZoneMap = useMemo(() => {
    if (event) {
      return true;
    }
    return pricingConfigFromEvents.data?.canUseZoneMap;
  }, [event, pricingConfigFromEvents.data?.canUseZoneMap]);

  const multiEvents = useMemo(() => {
    if (event) {
      return false;
    }
    return distinctEventIds.length > 1;
  }, [distinctEventIds.length, event]);

  const {
    openDialog: openBulkEditAutoPricingSettingsDialog,
    closeDialog: closeBulkEditAutoPricingSettingsDialog,
  } = useDialog<BulkEditAutoPricingSettingsDialogProps>(
    DialogId.BulkEditAutoPricingSettings,
    BulkEditAutoPricingSettingsDialog
  );

  const onSubmitAutoPricingRework = useCallback(
    (
      settingsInput: ListingDetailsAutoPricingSectionUpdatesV2 | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        tryInvokeApi(
          async () => {
            const client = await new BulkEditListingClient(
              activeAccountWebClientConfig
            );
            const preview = await client.bulkUpdateAutoPricingSettingsPreview(
              filterQueryWithEventIds
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateAutoPricingSettingsPreview', error, {
              trackErrorData: {
                filterQueryWithEventIds,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (settingsInput) {
        tryInvokeApi(
          async () => {
            const client = await new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            let previewMarketPrices: { [key: number]: number } | null = null;
            if (
              hasFixFloorRelativeToMarketFeature &&
              latestPreviewCalculationRef.current
            ) {
              const calculations =
                latestPreviewCalculationRef.current.listingPriceCalculations;
              if (calculations && calculations.length > 0) {
                previewMarketPrices = calculations.reduce(
                  (acc, calc) => {
                    if (calc.websitePrice?.amt != null) {
                      acc[calc.listingId] = calc.websitePrice.amt;
                    }
                    return acc;
                  },
                  {} as { [key: number]: number }
                );
              }
            }

            const finalSettingsInput =
              hasFixFloorRelativeToMarketFeature && previewMarketPrices
                ? { ...settingsInput, previewMarketPrices }
                : settingsInput;

            const input = {
              item1: preview!.preview,
              item2: finalSettingsInput,
            };

            const succeeded = await client.bulkUpdateAutoPricingSettingsV2(
              input,
              sanitizedEvent?.viagId,
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkEditAutoPricingSettingsDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkUpdateAutoPricingSettingsV2', error, {
              trackErrorData: {
                settingsInput,
                preview,
              },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeBulkEditAutoPricingSettingsDialog,
      sanitizedEvent?.viagId,
      filterQueryWithEventIds,
      refreshExpandedListItems,
      setActivePosEntity,
      setIsLoading,
      showErrorDialog,
      updateKey,
      hasFixFloorRelativeToMarketFeature,
    ]
  );

  const listings = useMemo(() => {
    return (eventsTransformed ?? [])
      .flatMap((ev) => ev.entities.listings)
      .flatMap(flattenListingGroup)
      .filter(({ id, isLtGrp }) => id > 0 && !isLtGrp) as Listing[];
  }, [eventsTransformed]);

  const openDialog = useCallback(() => {
    openBulkEditAutoPricingSettingsDialog({
      event: sanitizedEvent,
      updateKey: updateKey,
      isLoading: isLoading,
      canUseZoneMap: canUseZoneMap,
      multiEvents,
      listings: listings,
      selectedListingIds: affectedVisibleListingIds,
      onOkay: onSubmitAutoPricingRework,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditAutoPricingSettingsDialog,
      onPreviewCalculated: hasFixFloorRelativeToMarketFeature
        ? (result: BulkListingPriceCalculationResult | null) => {
            latestPreviewCalculationRef.current = result;
          }
        : undefined,
    });
  }, [
    affectedVisibleListingIds,
    canUseZoneMap,
    closeBulkEditAutoPricingSettingsDialog,
    hasFixFloorRelativeToMarketFeature,
    isLoading,
    listings,
    multiEvents,
    onSubmitAutoPricingRework,
    openBulkEditAutoPricingSettingsDialog,
    sanitizedEvent,
    setIsLoading,
    updateKey,
  ]);

  const openDialogStableRef = useEventCallback(() => {
    openDialog();
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditAutoPricingSettings,
        onSelect: openDialogStableRef,
      },
      disabled: !hasAutoPricingFeature || !hasEditPricingPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditAutoPricingSettings,
        disabled: !hasAutoPricingFeature || !hasEditPricingPermission,
      });
    };
  }, [
    hasAutoPricingFeature,
    hasEditPricingPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasAutoPricingFeature || !hasEditPricingPermission) {
      return [];
    }

    return [
      {
        disabled: isLoading,
        onClick: openDialog,
        title: ContentId.AutopricingSettings,
      },
    ];
  }, [hasAutoPricingFeature, hasEditPricingPermission, isLoading, openDialog]);

  return {
    dropDown: (
      <LaunchEditAutoPricingSettings
        key={`LaunchEditAutoPricingSettings`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openDialog();
        }}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
